/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Mantine
import { Box, Title } from '@mantine/core';

// Components
import { Button } from '@components/atoms/Button';
import { Select } from '@components/atoms/Select';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './AddSiteForm.module.css';

// Utils
import {
	convertArrayToLabelValue,
	convertQuantityByUnit,
	formatDate,
	getRatioUnit,
	getUnitFromProjectUnit,
} from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import {
	DatePicker,
	Divider,
	NumericInput,
	TextInput,
	Tooltip,
} from '@components/atoms';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/details/api/get-measurability-project-by-id';
import { TMesurabilityProject } from '@features/offsets/types';
import { useMediaQuery } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useOrganizations } from 'shared/api/get-organizations';
import { useProducers } from 'shared/api/get-producers';
import { useAddSite } from '../../../api/create-site';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddSiteForm = (props: {
	form: any;
	onCancel: () => void;
	onBack: () => void;
	isPreviousMeasurabilityProjectDetails: boolean;
}) => {
	const [organizationId, setOrganizationId] =
		useState('');
	const [producerId, setProducerId] = useState('');

	const { meterPointsPage, meterPointsPageSize } =
		useStore();

	const [
		bodyAddConversionFactor,
		setBodyAddConversionFactor,
	] = useState({});

	const { t } = useTranslation();

	const { projectId } = useParams();

	const {
		data: projectFromId,
		user,
		isFetching,
		isError,
		contactProducer,
		sites,
		isFetched,
	} = useMeasurabilityProject({
		projectId: projectId ? projectId : '',
		queryConfig: {
			enabled: !!projectId,
		},
	});

	const { data: projects } = useMeasurabilities({
		entity: producerId ? producerId : [],
	});
	const { data: organizations } = useOrganizations();

	const { data: producers } = useProducers({
		organization: organizationId ? organizationId : [],
	});

	const addSite = useAddSite(
		meterPointsPage,
		meterPointsPageSize,
		bodyAddConversionFactor
	);

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	useEffect(() => {
		if (addSite.isSuccess) {
			props.onBack();
		}
	}, [addSite.isSuccess]);

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			props.form.validate();

			const unitProject =
				projects.find(
					(_project: TMesurabilityProject) =>
						_project.id === props.form.values.project
				) &&
				projects.find(
					(_project: TMesurabilityProject) =>
						_project.id === props.form.values.project
				).unit
					? getUnitFromProjectUnit(
							projects.find(
								(_project: TMesurabilityProject) =>
									_project.id ===
									props.form.values.project
							).unit
						)
					: '';

			if (props.form.isValid()) {
				const {
					name,
					project,
					minValue,
					maxValue,
					country,
					place,
					ratio,
					startDate,
				} = props.form.values;

				const _minValue = convertQuantityByUnit(
					minValue,
					unitProject
				);

				const _maxValue = convertQuantityByUnit(
					maxValue,
					unitProject
				);

				const bodyAddSite = {
					name,
					project,
					production: {
						...(minValue !== undefined &&
							minValue !== 0 && {
								minValue: _minValue,
							}),
						...(maxValue !== undefined &&
							maxValue !== 0 && {
								maxValue: _maxValue,
							}),
					},
					...(country !== null &&
						country !== '' && {
							location: {
								...(country !== undefined && {
									countryCode: country,
								}),
								...(place !== undefined && { place }),
							},
						}),
				};

				const bodyAddConversionFactor = {
					...(ratio !== undefined && { ratio }),
					...(startDate !== undefined && {
						startDate: formatDate(
							startDate,
							'YYYY-MM-DD'
						),
					}),
				};

				setBodyAddConversionFactor(
					bodyAddConversionFactor
				);

				addSite.mutate(bodyAddSite);
			}
		};

		/* ******************************** CONSTANTS ******************************* */

		const requiredFields = [
			'organization',
			'entity',
			'site',
			'project',
		];
		const isValidSelects = requiredFields.every((field) =>
			props.form.isValid(field)
		);
		const isEnableToAdd = isValidSelects;

		const {
			onChange: onChangeProject,
			...organizationProps
		} = props.form.getInputProps('organization');

		const { onChange: onChangeSite, ...entityProps } =
			props.form.getInputProps('entity');

		const handleChangeEntity = (value: string) => {
			props.form.setFieldValue('entity', value);
			props.form.setFieldValue('project', null);

			setProducerId(value);
		};

		const {
			onChange: onChangeProject2,
			...projectProps
		} = props.form.getInputProps('project');

		const handleChangeProject = (value: string) => {
			props.form.setFieldValue('project', value);
		};

		const handleChangeOrganization = (value: string) => {
			props.form.setFieldValue('organization', value);
			setOrganizationId(value);
			props.form.setFieldValue('entity', null);
			props.form.setFieldValue('project', null);
		};

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (
				props.form.values &&
				props.form.values.project
			) {
				const projectId = props.form.values.project;
				if (projectId) {
					const foundCountry = projects.find(
						(_project: TMesurabilityProject) =>
							_project.id === projectId
					);
					if (
						foundCountry &&
						foundCountry.location &&
						foundCountry.location.place
					) {
						props.form.setValues({
							place: foundCountry.location.place,
						});
					}

					if (
						foundCountry &&
						foundCountry.location &&
						foundCountry.location.countryCode
					) {
						props.form.setValues({
							country: foundCountry.location.countryCode,
						});
					}
				}
			}
		}, [props.form.values.project]);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (isFetched) {
				props.form.setFieldValue(
					'organization',
					projectFromId.entity.organization
				);
				props.form.setFieldValue(
					'entity',
					projectFromId.entity.id
				);
				props.form.setFieldValue(
					'project',
					projectFromId.id
				);
			}
		}, [isFetched]);

		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(
					(values: any) => {}
				)}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{isFetched && projectFromId
							? projectFromId.name
							: t('global.sites')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('sites.addSite')}
					</Text>
				</Box>

				{/* Row for Producer */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								props.isPreviousMeasurabilityProjectDetails
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								organizations
							)}
							placeholder={t('global.selectOrganization')}
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
					</Box>
				</Box>
				{/* Row for Project & Site */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								!props.form.isValid('organization') ||
								props.isPreviousMeasurabilityProjectDetails
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.producer')}
							withAsterisk
							data={convertArrayToLabelValue(producers)}
							placeholder={t('global.selectEntity')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								!props.form.isValid('entity') ||
								props.isPreviousMeasurabilityProjectDetails
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.project')}
							withAsterisk
							data={convertArrayToLabelValue(projects)}
							placeholder={t('global.selectProject')}
							onChange={handleChangeProject}
							{...projectProps}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				{/* Row for Site name */}
				<Box className={classes.containerInput}>
					<TextInput
						withAsterisk
						size={isBigScreen ? 'md' : 'sm'}
						label={t('global.name')}
						placeholder={t('global.name')}
						{...props.form.getInputProps('name')}
					/>
				</Box>
				{/* Row for Country & Place */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.country')}
							data={EUROPEAN_COUNTRIES}
							placeholder={t('global.country')}
							{...props.form.getInputProps('country')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TextInput
							disabled={!props.form.values.country}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.place')}
							placeholder={t('global.place')}
							{...props.form.getInputProps('place')}
						/>
					</Box>
				</Box>
				{/* Row for MinValue & MaxValue */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<NumericInput
							unit={
								projects.find(
									(_project: TMesurabilityProject) =>
										_project.id ===
										props.form.values.project
								) &&
								projects.find(
									(_project: TMesurabilityProject) =>
										_project.id ===
										props.form.values.project
								).unit
									? getUnitFromProjectUnit(
											projects.find(
												(
													_project: TMesurabilityProject
												) =>
													_project.id ===
													props.form.values.project
											).unit
										)
									: ''
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('sites.form.labelMinProduction')}
							defaultValue={0}
							{...props.form.getInputProps('minValue')}
						/>
					</Box>

					<Box className={classes.containerInput}>
						<NumericInput
							size={isBigScreen ? 'md' : 'sm'}
							label={t('sites.form.labelMaxProduction')}
							defaultValue={0}
							unit={
								projects.find(
									(_project: TMesurabilityProject) =>
										_project.id ===
										props.form.values.project
								) &&
								projects.find(
									(_project: TMesurabilityProject) =>
										_project.id ===
										props.form.values.project
								).unit
									? getUnitFromProjectUnit(
											projects.find(
												(
													_project: TMesurabilityProject
												) =>
													_project.id ===
													props.form.values.project
											).unit
										)
									: ''
							}
							{...props.form.getInputProps('maxValue')}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				<Box className={classes.rowFcDetails}>
					<Title className={classes.titleFcDetails}>
						{t('global.fcDetails')}
					</Title>
				</Box>
				{/* Row for FC details */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<NumericInput
							withAsterisk={
								!!props.form.getValues().startDate
							}
							max={15000}
							step={1}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.ratio')}
							unit={
								projects.find(
									(_project: TMesurabilityProject) =>
										_project.id ===
										props.form.values.project
								) &&
								projects.find(
									(_project: TMesurabilityProject) =>
										_project.id ===
										props.form.values.project
								).unit
									? getRatioUnit(
											projects.find(
												(
													_project: TMesurabilityProject
												) =>
													_project.id ===
													props.form.values.project
											).unit
										)
									: ''
							}
							defaultValue={0}
							{...props.form.getInputProps('ratio')}
						/>
					</Box>
					<Tooltip
						isActive={true}
						label={t(
							'sites.form.dateMustLessThanFirstProduction'
						)}
					>
						<Box className={classes.containerInput}>
							<DatePicker
								withAsterisk={
									!!props.form.getValues().ratio
								}
								clearable
								label={t('global.startDate')}
								placeholder="DD/MM/YYYY"
								onChange={() => {}}
								maxDate={
									props.form.getInputProps('endDate')
										.value == null
										? dayjs().subtract(1, 'day').toDate()
										: dayjs(
												props.form.getInputProps(
													'endDate'
												).value
											).toDate()
								}
								{...props.form.getInputProps('startDate')}
							/>
						</Box>
					</Tooltip>
				</Box>
				<Divider size={'xs'} />
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							data-autofocus
							disabled={!isEnableToAdd}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={handleSubmitForm}
							loading={addSite.isLoading}
						>
							{t('sites.addSite')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
